import type {
  EmailPipelineAttachment,
  EmailPipelineInitialFilters,
  EmailPipelineProcessingInstance,
  EmailPipelineRecord,
  EmailPipelineRecordsResponse,
} from '../../../types/features/emailPipeline';
import { parseDateParam } from '../../../utils/normalizers/dateNormalizers';
import { getErrorMessage } from '../../../utils/normalizers/errorNormalizers';
import { parseNonNegativeNumber, parsePositiveInt } from '../../../utils/normalizers/numberNormalizers';
import { EMAIL_PIPELINE_STATUS_OPTIONS } from './emailPipelineFormatters';
import {
  EMAIL_PIPELINE_DATE_FROM_PARAM,
  EMAIL_PIPELINE_DATE_TO_PARAM,
  EMAIL_PIPELINE_PAGE_PARAM,
  EMAIL_PIPELINE_STATUS_PARAM,
  EMAIL_PIPELINE_STORE_PARAM,
} from '../utils/emailPipelineQueryParams';

const DEFAULT_PAGE = 1;

export const PAGE_SIZE = 50;

const allowedStatuses = new Set(
  EMAIL_PIPELINE_STATUS_OPTIONS
    .map((option) => option.value)
    .filter((value): value is string => Boolean(value)),
);

export const parseInitialFilters = (searchParams: URLSearchParams): EmailPipelineInitialFilters => ({
  page: parsePositiveInt(searchParams.get(EMAIL_PIPELINE_PAGE_PARAM), DEFAULT_PAGE),
  status: (() => {
    const raw = (searchParams.get(EMAIL_PIPELINE_STATUS_PARAM) || '').trim();
    if (!raw) {
      return '';
    }
    return allowedStatuses.has(raw) ? raw : '';
  })(),
  storeId: searchParams.get(EMAIL_PIPELINE_STORE_PARAM) || '',
  dateRange: [
    parseDateParam(searchParams.get(EMAIL_PIPELINE_DATE_FROM_PARAM)),
    parseDateParam(searchParams.get(EMAIL_PIPELINE_DATE_TO_PARAM)),
  ],
});

export const getTotalRecordsFromResponse = (response: EmailPipelineRecordsResponse): number => {
  const records = response?.result?.records || [];
  const explicitTotal =
    parseNonNegativeNumber(response?.result?.pagination?.total) ??
    parseNonNegativeNumber(response?.result?.total_count) ??
    parseNonNegativeNumber(response?.result?.count);
  const totalPages = parseNonNegativeNumber(response?.result?.total_pages);
  const inferredFromPages = totalPages ? totalPages * PAGE_SIZE : null;

  return explicitTotal ?? inferredFromPages ?? records.length;
};

export { parseDateParam, getErrorMessage, parseNonNegativeNumber, parsePositiveInt };

export const getSortableTimestamp = (record: EmailPipelineRecord): number => {
  const candidate = record.processed_at || record.updated_at || record.created_at || record.received_at;
  const parsed = candidate ? new Date(candidate).getTime() : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getEmailPipelineAttachments = (record: EmailPipelineRecord | null | undefined): EmailPipelineAttachment[] => {
  if (Array.isArray(record?.attachments)) {
    return record?.attachments || [];
  }

  if (
    record?.normalized_payload &&
    typeof record.normalized_payload === 'object' &&
    Array.isArray(record.normalized_payload.attachments)
  ) {
    return record.normalized_payload.attachments;
  }

  return [];
};

export const getEmailPipelineProcessingInstances = (
  records: EmailPipelineRecord[],
  messageId?: string,
): EmailPipelineProcessingInstance[] =>
  records
    .filter((record) => record.message_id === messageId)
    .sort((a, b) => getSortableTimestamp(a) - getSortableTimestamp(b))
    .map((record, index) => ({
      attempt: index + 1,
      processed_at: record.processed_at || record.updated_at || record.created_at,
      status: record.status,
      log_message: record.log_message,
    }));
